import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SubmissionTypeSelector } from "@/components/contributor/SubmissionTypeSelector";
import { SocialSubmissionForm } from "@/components/contributor/SocialSubmissionForm";
import { BlogSubmissionForm } from "@/components/contributor/BlogSubmissionForm";
import { SpotlightCreditsSection } from "@/components/creator/SpotlightCreditsSection";
import DOMPurify from "dompurify";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function Creator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submissionType, setSubmissionType] = useState<'social' | 'blog' | null>(null);
  const [yearlyLimits, setYearlyLimits] = useState({
    blog: { used: 0, max: 15 },
    social: { used: 0, max: 30 }
  });
  const [rewardAmounts, setRewardAmounts] = useState({
    blog: 10,
    social: 5
  });

  useEffect(() => {
    loadSubmissions();
    loadRewardSettings();
  }, []);

  useEffect(() => {
    calculateYearlyLimits();
  }, [submissions]);

  const calculateYearlyLimits = () => {
    const currentYear = new Date().getFullYear();
    const approvedThisYear = submissions.filter(
      s => s.review_status === 'approved' && 
           s.credits_awarded === true &&
           s.submission_year === currentYear
    );
    
    setYearlyLimits({
      blog: {
        used: approvedThisYear.filter(s => s.content_type === 'blog_article').length,
        max: 15
      },
      social: {
        used: approvedThisYear.filter(s => s.content_type !== 'blog_article').length,
        max: 30
      }
    });
  };

  const loadRewardSettings = async () => {
    try {
      const { data: settingsData } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'creator_rewards')
        .maybeSingle();

      if (settingsData?.value) {
        const rewards = settingsData.value as { blog_article?: number; social_content?: number };
        setRewardAmounts({
          blog: rewards.blog_article || 10,
          social: rewards.social_content || 5
        });
      }
    } catch (error) {
      console.error("Error loading reward settings:", error);
    }
  };

  const loadSubmissions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("creator_submissions")
        .select("*")
        .eq("creator_id", user.id)
        .order("submission_date", { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error("Error loading submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSubmit = async (data: any) => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let screenshotUrl = null;
      if (data.screenshot) {
        const fileExt = data.screenshot.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('profile-images')
          .upload(fileName, data.screenshot);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('profile-images')
          .getPublicUrl(fileName);

        screenshotUrl = publicUrl;
      }

      const { error } = await supabase
        .from("creator_submissions")
        .insert({
          creator_id: user.id,
          content_type: data.platform,
          title: data.title,
          content_url: data.url,
          content_description: data.notes,
          screenshot_url: screenshotUrl,
          review_status: 'pending',
          reward_amount: rewardAmounts.social,
          submission_year: new Date().getFullYear()
        });

      if (error) throw error;

      toast.success("Contenuto social inviato con successo!");
      setSubmissionType(null);
      loadSubmissions();
    } catch (error: any) {
      console.error("Submission error:", error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBlogSubmit = async (data: any) => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      let featuredImageUrl = null;
      if (data.featuredImage) {
        const fileExt = data.featuredImage.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('cover-images')
          .upload(fileName, data.featuredImage);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('cover-images')
          .getPublicUrl(fileName);

        featuredImageUrl = publicUrl;
      }

      const sanitizedHtml = DOMPurify.sanitize(data.contentHtml);

      const { error } = await supabase
        .from("creator_submissions")
        .insert({
          creator_id: user.id,
          content_type: 'blog_article',
          title: data.title,
          meta_description: data.metaDescription,
          content_html: sanitizedHtml,
          content_plain_text: data.contentPlainText,
          word_count: data.wordCount,
          featured_image_url: featuredImageUrl,
          tags: data.tags,
          content_description: data.notes,
          review_status: 'pending',
          reward_amount: rewardAmounts.blog,
          submission_year: new Date().getFullYear()
        });

      if (error) throw error;

      toast.success("Articolo blog inviato con successo!");
      setSubmissionType(null);
      loadSubmissions();
    } catch (error: any) {
      console.error("Submission error:", error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const stats = {
    totalSubmissions: submissions.length,
    approved: submissions.filter(s => s.review_status === 'approved').length,
    earned: submissions
      .filter(s => s.review_status === 'approved')
      .reduce((sum, s) => sum + parseFloat(s.reward_amount || 0), 0)
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: "bg-warning text-warning-foreground",
      approved: "bg-success text-success-foreground",
      rejected: "bg-destructive text-destructive-foreground"
    };

    return (
      <Badge className={statusColors[status] || ""}>
        {t(`creator.status.${status}`)}
      </Badge>
    );
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p className="font-heading text-xl">{t("common.loading")}...</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">{t("common.dashboard")}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("creator.pageTitle")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Back Button + Title */}
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          <h1 className="font-heading text-3xl font-bold text-dark">
            {t("creator.pageTitle")}
          </h1>
        </div>
        
        <p className="text-muted-foreground mb-8 ml-14">
          {t("creator.pageDescription")}
        </p>

        {/* Spotlight Credits Section */}
        <SpotlightCreditsSection />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <BrutalCard className="p-6">
            <div className="text-3xl font-heading mb-2">{stats.totalSubmissions}</div>
            <div className="text-sm text-muted-foreground">{t("creator.stats.totalSubmissions")}</div>
          </BrutalCard>
          <BrutalCard className="p-6">
            <div className="text-3xl font-heading mb-2">{stats.approved}</div>
            <div className="text-sm text-muted-foreground">{t("creator.stats.approved")}</div>
            <div className="text-xs text-success mt-2 font-bold">
              📝 €{rewardAmounts.blog} per articolo • 📱 €{rewardAmounts.social} per post
            </div>
          </BrutalCard>
          <BrutalCard className="p-6">
            <div className="text-3xl font-heading mb-2">€{stats.earned.toFixed(2)}</div>
            <div className="text-sm text-muted-foreground">{t("creator.stats.totalEarned")}</div>
          </BrutalCard>
        </div>

        {/* Limiti Annuali */}
        <BrutalCard className="mb-8 p-6 bg-warning/10">
          <h2 className="font-heading text-lg mb-4">📊 {t("creator.yearlyLimits.title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold">📝 {t("creator.blogArticles")}</span>
                <span className="text-sm">{yearlyLimits.blog.used}/{yearlyLimits.blog.max}</span>
              </div>
              <div className="h-3 bg-muted border-2 border-dark">
                <div 
                  className={`h-full transition-all ${yearlyLimits.blog.used >= yearlyLimits.blog.max ? 'bg-destructive' : 'bg-success'}`}
                  style={{ width: `${(yearlyLimits.blog.used / yearlyLimits.blog.max) * 100}%` }}
                />
              </div>
              {yearlyLimits.blog.used >= yearlyLimits.blog.max && (
                <p className="text-xs text-destructive mt-1">{t("creator.yearlyLimits.blogMaxReached")}</p>
              )}
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold">📱 {t("creator.socialPosts")}</span>
                <span className="text-sm">{yearlyLimits.social.used}/{yearlyLimits.social.max}</span>
              </div>
              <div className="h-3 bg-muted border-2 border-dark">
                <div 
                  className={`h-full transition-all ${yearlyLimits.social.used >= yearlyLimits.social.max ? 'bg-destructive' : 'bg-success'}`}
                  style={{ width: `${(yearlyLimits.social.used / yearlyLimits.social.max) * 100}%` }}
                />
              </div>
              {yearlyLimits.social.used >= yearlyLimits.social.max && (
                <p className="text-xs text-destructive mt-1">{t("creator.yearlyLimits.socialMaxReached")}</p>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {t("creator.yearlyLimits.explanation")}
          </p>
        </BrutalCard>

        {!submissionType ? (
          <SubmissionTypeSelector onSelectType={setSubmissionType} />
        ) : submissionType === 'social' ? (
          <>
            {/* Box "Come funziona" SOCIAL */}
            <BrutalCard className="mb-6 p-6 bg-primary/10">
              <h2 className="font-heading text-lg mb-3 flex items-center gap-2">
                📱 {t("creator.socialPosts")} - {t("creator.howItWorks")}
              </h2>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>✓ {t("creator.socialForm.platforms")}</li>
                <li>✓ {t("creator.socialForm.tagRequired")}</li>
                <li>✓ {t("creator.socialForm.screenshotRequired")}</li>
                <li>✓ {t("creator.socialForm.urlRequired")}</li>
              </ul>
              <div className="mt-4 p-3 bg-success/20 border-2 border-success rounded">
                <p className="text-sm font-bold text-success-foreground">
                  💰 €{rewardAmounts.social} per contenuto approvato
                </p>
                <p className="text-xs mt-1 text-muted-foreground">
                  {t("creator.spotlightOnlyInfo")}
                </p>
              </div>
            </BrutalCard>
            
            <SocialSubmissionForm 
              onBack={() => setSubmissionType(null)}
              onSubmit={handleSocialSubmit}
              isSubmitting={submitting}
            />
          </>
        ) : (
          <>
            {/* Box "Come funziona" BLOG */}
            <BrutalCard className="mb-6 p-6 bg-primary/10">
              <h2 className="font-heading text-lg mb-3 flex items-center gap-2">
                📝 {t("creator.blogArticles")} - {t("creator.howItWorks")}
              </h2>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>✓ {t("creator.blogForm.minWords")}</li>
                <li>✓ {t("creator.blogForm.themeRequired")}</li>
                <li>✓ {t("creator.blogForm.featuredImageRequired")}</li>
                <li>✓ {t("creator.blogForm.tagsRequired")}</li>
              </ul>
              <div className="mt-4 p-3 bg-success/20 border-2 border-success rounded">
                <p className="text-sm font-bold text-success-foreground">
                  💰 €{rewardAmounts.blog} per articolo approvato
                </p>
                <p className="text-xs mt-1 text-muted-foreground">
                  {t("creator.spotlightOnlyInfo")}
                </p>
              </div>
            </BrutalCard>
            
            <BlogSubmissionForm 
              onBack={() => setSubmissionType(null)}
              onSubmit={handleBlogSubmit}
              isSubmitting={submitting}
            />
          </>
        )}

        <BrutalCard className="p-6">
          <h2 className="font-heading text-2xl mb-6">{t("creator.yourContent")}</h2>
          
          {submissions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t("creator.noContent")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-[3px] border-dark">
                    <th className="text-left py-2">{t("creator.date")}</th>
                    <th className="text-left">{t("creator.type")}</th>
                    <th className="text-left">{t("creator.submissionTitle")}</th>
                    <th className="text-center">{t("creator.status.label")}</th>
                    <th className="text-right">{t("creator.reward")}</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr key={submission.id} className="border-b-2 border-dark/20">
                      <td className="py-3">
                        {format(new Date(submission.submission_date), "dd/MM/yyyy")}
                      </td>
                      <td>
                        <Badge variant="outline">{submission.content_type}</Badge>
                      </td>
                      <td className="font-medium">{submission.title}</td>
                      <td className="text-center">{getStatusBadge(submission.review_status)}</td>
                      <td className="text-right font-bold">
                        €{parseFloat(submission.reward_amount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </BrutalCard>
      </main>
      
      <Footer />
    </div>
  );
}
